import { getCurrentApi } from './api';
import { User, CreateUserRequest, UpdateUserRequest, ChangePasswordRequest, UserRole } from '../models/User';
import { auditService } from './auditService';
import * as firebaseService from './firebaseService';

export const getUsers = async (): Promise<User[]> => {
  const api = getCurrentApi();
  const response = await api.get<User[]>('/users');
  return response.data;
};

export const getUsersByHotel = async (hotelId: number): Promise<User[]> => {
  const api = getCurrentApi();
  const response = await api.get<User[]>(`/users/hotel/${hotelId}`);
  return response.data;
};

export const getUserById = async (id: number): Promise<User> => {
  const api = getCurrentApi();
  const response = await api.get<User>(`/users/${id}`);
  return response.data;
};

export const createUser = async (user: CreateUserRequest): Promise<User> => {
  const api = getCurrentApi();
  const response = await api.post<User>('/users', user);
  
  // Log the action to Firebase
  await auditService.logAction('User Created', {
    additionalInfo: `Created user: ${response.data.username} with role: ${response.data.role}`
  });
  
  return response.data;
};

export const deleteUser = async (id: number): Promise<void> => {
  // Get user info before deleting for audit log and Firebase sync
  const user = await getUserById(id);
  
  const api = getCurrentApi();
  await api.delete(`/users/${id}`);
  
  // Sync with Firebase - remove user from Firebase
  if (user.hotelId) {
    try {
      await firebaseService.deleteUserFromFirebase(user.username, user.hotelId);
    } catch (error) {
      console.error('Error syncing user deletion with Firebase:', error);
      // Continue even if Firebase sync fails
    }
  }
  
  // Log the action to Firebase
  await auditService.logAction('User Deleted', {
    additionalInfo: `Deleted user: ${user.username} (Role: ${user.role})`
  });
};

export const updateUser = async (id: number, user: UpdateUserRequest): Promise<User> => {
  // Get the original user data before update for Firebase sync
  const originalUser = await getUserById(id);
  
  const api = getCurrentApi();
  const response = await api.put<User>(`/users/${id}`, user);
  
  // Sync with Firebase if hotel user
  if (response.data.hotelId) {
    try {
      const newRoleString = UserRole[response.data.role];
      await firebaseService.updateUserInFirebase(
        originalUser.username,
        response.data.hotelId,
        response.data.username !== originalUser.username ? response.data.username : undefined,
        newRoleString !== UserRole[originalUser.role] ? newRoleString : undefined
      );
    } catch (error) {
      console.error('Error syncing user update with Firebase:', error);
      // Continue even if Firebase sync fails
    }
  }
  
  // Log the action to Firebase
  await auditService.logAction('User Updated', {
    additionalInfo: `Updated user: ${response.data.username} (ID: ${id})`
  });
  
  return response.data;
};

export const changePassword = async (id: number, passwordData: ChangePasswordRequest): Promise<void> => {
  const api = getCurrentApi();
  await api.put(`/users/${id}/password`, passwordData);
  
  // Log the action to Firebase
  await auditService.logAction('Password Changed', {
    additionalInfo: `Password changed for user ID: ${id}`
  });
};